import { type Employee, EmployeeCreateSchema, type EmployeeCreate, type EmployeeUpdate, EmployeeUpdateSchema } from '@gestion-permisos/contracts';
import { type FormEvent, useState } from 'react';
import { ChoiceList } from '../components/choice-list.js';
import { ProblemMessage } from '../components/problem-message.js';
import { Checkbox, describedBy, Field, INPUT, Notice, nullIfBlank, PRIMARY_BUTTON } from './admin-ui.js';
import { OPTIONS_PAGE_SIZE, useEmployeeFormOptions } from './people-api.js';

interface Draft {
  fullName: string;
  email: string;
  upn: string;
  entraObjectId: string;
  jobTitle: string;
  areaId: string;
  managerId: string;
  isDirectorGeneral: boolean;
  active: boolean;
}

type DraftField = keyof Draft;
type Errors = Partial<Record<DraftField, string>>;

/** Mensajes de la validación de **forma** (esquema del contrato). Las reglas las decide la API. */
const MESSAGES: Partial<Record<DraftField, string>> = {
  fullName: 'Escribe el nombre completo (hasta 200 caracteres).',
  email: 'Escribe un correo válido.',
  upn: 'Escribe un UPN con el formato usuario@dominio.',
  entraObjectId: 'El Object ID debe ser un GUID (o quedar vacío).',
  jobTitle: 'El cargo admite hasta 200 caracteres.',
  areaId: 'Selecciona un área.',
  managerId: 'Selecciona un responsable válido.',
};

function errorsOf(issues: { path: PropertyKey[] }[]): Errors {
  const errors: Errors = {};
  for (const issue of issues) {
    const field = issue.path[0];
    if (typeof field === 'string' && field in MESSAGES) errors[field as DraftField] = MESSAGES[field as DraftField];
  }
  return errors;
}

export function draftOf(employee?: Employee): Draft {
  return {
    fullName: employee?.fullName ?? '',
    email: employee?.email ?? '',
    upn: employee?.upn ?? '',
    entraObjectId: employee?.entraObjectId ?? '',
    jobTitle: employee?.jobTitle ?? '',
    areaId: employee?.areaId ?? '',
    managerId: employee?.managerId ?? '',
    isDirectorGeneral: employee?.isDirectorGeneral ?? false,
    active: employee?.active ?? true,
  };
}

/** Valores para el contrato: textos opcionales vacíos → `null`; sin responsable → `null`. */
function valuesOf(draft: Draft) {
  return {
    fullName: draft.fullName,
    email: draft.email.trim(),
    upn: draft.upn,
    entraObjectId: nullIfBlank(draft.entraObjectId)?.trim() ?? null,
    jobTitle: nullIfBlank(draft.jobTitle),
    areaId: draft.areaId,
    managerId: draft.managerId === '' ? null : draft.managerId,
    isDirectorGeneral: draft.isDirectorGeneral,
  };
}

export interface EmployeeFormProps {
  readonly employee?: Employee;
  readonly pending: boolean;
  readonly error: unknown;
  readonly notice?: string | undefined;
  readonly submitLabel: string;
  /** Alta: el cuerpo completo validado con `EmployeeCreateSchema`. */
  readonly onCreate?: (body: EmployeeCreate) => void;
  /** Edición: solo los campos cambiados, validados con `EmployeeUpdateSchema`. */
  readonly onUpdate?: (changes: EmployeeUpdate) => void;
}

/**
 * Formulario de empleado (W8). Se editan también `upn`, `email` y el Object ID opcional (campos del
 * contrato de administración). El área y el responsable salen de una página de 100 de `/areas` y
 * `/employees`; si hay más, se indica. **El cliente no comprueba** cuántos Directores Generales hay, el responsable
 * obligatorio, los ciclos ni el último administrador: los 409 y 422 de la API se muestran tal cual.
 */
export function EmployeeForm({ employee, pending, error, notice, submitLabel, onCreate, onUpdate }: EmployeeFormProps) {
  const { areas, managers } = useEmployeeFormOptions();
  const [draft, setDraft] = useState<Draft>(draftOf(employee));
  const [errors, setErrors] = useState<Errors>({});
  const [ownNotice, setOwnNotice] = useState<string | undefined>(undefined);
  const set = (next: Partial<Draft>) => setDraft((current) => ({ ...current, ...next }));
  const prefix = employee ? 'edit-employee' : 'employee';
  const id = (field: DraftField) => `${prefix}-${field}`;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setOwnNotice(undefined);
    const values = valuesOf(draft);
    if (!employee) {
      const parsed = EmployeeCreateSchema.safeParse(values);
      if (!parsed.success) return setErrors(errorsOf(parsed.error.issues));
      setErrors({});
      onCreate?.(parsed.data);
      return;
    }
    const original = valuesOf(draftOf(employee));
    const changes: Record<string, unknown> = {};
    for (const key of Object.keys(values) as (keyof typeof values)[]) {
      if (values[key] !== original[key]) changes[key] = values[key];
    }
    if (draft.active !== employee.active) changes.active = draft.active;
    if (Object.keys(changes).length === 0) {
      setOwnNotice('No hay cambios que guardar.');
      return;
    }
    const parsed = EmployeeUpdateSchema.safeParse(changes);
    if (!parsed.success) return setErrors(errorsOf(parsed.error.issues));
    setErrors({});
    onUpdate?.(parsed.data);
  };

  const text = (field: 'fullName' | 'email' | 'upn' | 'entraObjectId' | 'jobTitle', label: string) => (
    <Field id={id(field)} label={label} error={errors[field]}>
      <input id={id(field)} value={draft[field]} onChange={(event) => set({ [field]: event.target.value })} className={INPUT} {...describedBy(id(field), errors[field])} />
    </Field>
  );

  return (
    <form noValidate onSubmit={submit} className="max-w-2xl space-y-3" aria-label={employee ? 'Editar empleado' : 'Nuevo empleado'}>
      <Notice text={ownNotice ?? notice} />
      {error !== null && error !== undefined ? <ProblemMessage error={error} /> : null}
      <div className="grid gap-3 sm:grid-cols-2">
        {text('fullName', 'Nombre completo')}
        {text('email', 'Correo')}
        {text('upn', 'UPN')}
        {text('entraObjectId', 'Object ID de Entra (opcional)')}
        {text('jobTitle', 'Cargo (opcional)')}
        <Field id={id('areaId')} label="Área" error={errors.areaId}>
          <ChoiceList
            id={id('areaId')}
            value={draft.areaId}
            options={[
              { value: '', label: 'Selecciona…' },
              ...(areas.data?.items.map((area) => ({ value: area.id, label: area.active ? area.name : `${area.name} (inactiva)` })) ?? []),
            ]}
            onChange={(value) => set({ areaId: value })}
            className={INPUT}
            {...describedBy(id('areaId'), errors.areaId)}
          />
          {areas.data && areas.data.total > OPTIONS_PAGE_SIZE ? (
            <p className="mt-1 text-sm text-amber-800">
              Hay {areas.data.total} áreas; solo se muestran las {OPTIONS_PAGE_SIZE} primeras.
            </p>
          ) : null}
        </Field>
        <Field id={id('managerId')} label="Responsable" error={errors.managerId}>
          <ChoiceList
            id={id('managerId')}
            value={draft.managerId}
            options={[
              { value: '', label: 'Sin responsable' },
              ...(managers.data?.items.map((person) => ({ value: person.id, label: person.active ? person.fullName : `${person.fullName} (inactivo)` })) ?? []),
            ]}
            onChange={(value) => set({ managerId: value })}
            className={INPUT}
            {...describedBy(id('managerId'), errors.managerId)}
          />
          {managers.data && managers.data.total > OPTIONS_PAGE_SIZE ? (
            <p className="mt-1 text-sm text-amber-800">
              Hay {managers.data.total} empleados; solo se muestran los {OPTIONS_PAGE_SIZE} primeros.
            </p>
          ) : null}
        </Field>
      </div>
      {areas.error ? <ProblemMessage error={areas.error} /> : null}
      {managers.error ? <ProblemMessage error={managers.error} /> : null}
      <Checkbox id={id('isDirectorGeneral')} label="Director General" checked={draft.isDirectorGeneral} onChange={(value) => set({ isDirectorGeneral: value })} />
      {employee ? <Checkbox id={id('active')} label="Activo" checked={draft.active} onChange={(value) => set({ active: value })} /> : null}
      <button type="submit" disabled={pending} className={PRIMARY_BUTTON}>
        {submitLabel}
      </button>
    </form>
  );
}
